import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { alpha, Box, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { Spinner } from 'react-bootstrap';

const SwitchBox = ({
  items,              // [{ title: "روشن", value: "on", color: "#4caf50" }, ...]
  value,              // value آیتم انتخاب شده
  defaultValue,       // مقدار پیش‌فرض برای حالت uncontrolled
  onChange,           // (value, item) => {}
  noShadow,
  noTransForm,
  label,
  loading,
  disable,
  mobileStyle,
  ...props
}) => {
  const itemsRef = useRef([]);
  const [sliderStyle, setSliderStyle] = useState({});

  // state داخلی برای حالت uncontrolled
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? items?.[0]?.value
  );

  // تشخیص controlled یا uncontrolled
  const isControlled = value !== undefined;
  const selectedValue = isControlled ? value : internalValue;

  // پیدا کردن آیتم انتخاب شده بر اساس value
  const selectedItem = useMemo(() => {
    return items?.find(item => item.value === selectedValue);
  }, [items, selectedValue]);

  // پیدا کردن index آیتم انتخاب شده
  const selectedIndex = useMemo(() => {
    return items?.findIndex(item => item.value === selectedValue);
  }, [items, selectedValue]);

  // رنگ‌ها بر اساس index (برای CSS variables)
  const colors = useMemo(() => {
    const cl = {};
    items?.forEach((element, index) => {
      cl[`--color-${index}`] = element.color;
      cl[`--color-shadow-${index}`] = alpha(element.color, 0.4);
      cl[`--color-hover-shadow-${index}`] = alpha(element.color, 0.6);
    });
    return cl;
  }, [items]);

  // 🔥 استفاده از ResizeObserver برای محاسبه دقیق
  useLayoutEffect(() => {
    if (selectedIndex === -1 || !itemsRef.current[selectedIndex]) {
      return;
    }

    const element = itemsRef.current[selectedIndex];

    // تابع محاسبه استایل
    const updateSliderStyle = () => {
      setSliderStyle({
        left: element.offsetLeft,
        width: element.offsetWidth,
      });
    };

    // محاسبه اولیه
    updateSliderStyle();

    // ایجاد ResizeObserver برای رصد تغییرات
    const observer = new ResizeObserver(() => {
      updateSliderStyle();
    });

    // شروع رصد
    observer.observe(element);

    // پاکسازی هنگام unmount
    return () => {
      observer.disconnect();
    };
  }, [selectedIndex]);

  // هندل کلیک
  const handleItemClick = (item) => {
    // اگه uncontrolled هست، state داخلی رو آپدیت کن
    if (!isControlled) {
      setInternalValue(item.value);
    }

    // به parent اطلاع بده
    onChange?.(item.value, item);
  };
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      padding: 0,
      flexDirection: isMobile || mobileStyle ? 'column' : "row",
      align: "center",
      gap: isMobile || mobileStyle ? 0 : 2,
      width: "fit-content",
      flexShrink: 0,
      userSelect: "none"
    }}
      {...props}
    >
      {!!label && <p style={{ fontSize: isMobile||mobileStyle ? 10 : 14, fontWeight: 700 }} className={`m-0 ${isMobile||mobileStyle && "mb-1"}`} >{label}:</p>}
      <Box
        style={colors}
        sx={{
          display: "flex",
          alignItems: "center",
          p: "4px",
          gap: 1,
          position: 'relative',
          backgroundColor: 'white',
          border: `var(--color-${selectedIndex}) solid 1px`,
          borderRadius: "6px",
          cursor: 'pointer',
          transition: 'all 0.4s ease',
          boxShadow: !noShadow && `0 4px 15px var(--color-shadow-${selectedIndex})`,
          '&:hover': {
            transform: !noTransForm && 'translateY(-3px)',
            boxShadow: !noShadow && `0 6px 20px var(--color-hover-shadow-${selectedIndex})`,
          }
        }}
      >
        {loading && <div style={{ inset: -2, zIndex: 11 }} className="d-flex justify-content-center bg-white opacity-60 align-items-center position-absolute">
          <Spinner animation="border" size="md" />
        </div>}
        {disable && <div style={{ inset: -2, zIndex: 11 }} className="d-flex justify-content-center bg-white opacity-20 align-items-center position-absolute" />}
        {/* دایره متحرک */}
        <Box
          sx={{
            position: 'absolute',
            top: 2,
            left: sliderStyle.left - 1 ?? 0,
            width: sliderStyle.width + 2 ?? 0,
            bottom: 2,
            backgroundColor: `var(--color-${selectedIndex})`,
            borderRadius: '6px',
            transition: 'all 0.4s ease',
            boxShadow: !noShadow && '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >

        </Box>
        {items?.map((item, index) =>
          <Typography
            key={index}
            ref={(el) =>
              (itemsRef.current[index] = el)
            }
            onClick={() => handleItemClick(item)}
            sx={{
              color: selectedItem?.value == item.value ? "white" : `var(--color-${index})`,
              fontWeight: 'bold',
              fontSize: '16px',
              px: "4px",
              zIndex: 10,
              transition: 'opacity 0.3s',
              borderRadius: 1,
              transition: 'all 0.2s ease',
              border: `${selectedItem?.value == item.value ? `var(--color-${index})` : "white"} solid 1px`,
              '&:hover': {
                borderColor: `var(--color-${index})`,
              }
            }}
          >
            {item.title}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default memo(SwitchBox);